
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MockAPI } from '../services/api';
import { useAuth } from '../services/authContext';
import { Asset, AssetStatus } from '../types';
import { Card, Button, Badge } from '../components/ui';
import { Truck, Plus, ChevronRight, Search, ArrowUpDown, ArrowUp, ArrowDown, Calendar, AlertCircle, Filter } from 'lucide-react';

// Extended interface for display
interface AssetTableRow extends Asset {
  lastMaintenanceDate?: string;
  nextMaintenanceDate?: string;
  overdue: boolean;
}

type SortField = 'name' | 'status' | 'purchase_date' | 'lastMaintenanceDate' | 'nextMaintenanceDate';
type SortDirection = 'asc' | 'desc';

const AssetList: React.FC = () => {
  const { privileges } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<AssetTableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter & Sort State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    const load = async () => {
        setLoading(true);
        setError(null);
        // Load Assets and compute maintenance dates
        try {
            const assets = await MockAPI.getAssets();
            
            // This could be slow for many assets, normally we'd optimize or paginate at API level
            const rows = await Promise.all(assets.map(async (asset) => {
                const tasks = await MockAPI.getTasksForAsset(asset.asset_id);
                
                const completedTasks = tasks
                    .filter(t => t.completed_date)
                    .sort((a, b) => (b.completed_date!).localeCompare(a.completed_date!));
                const lastDate = completedTasks.length > 0 ? completedTasks[0].completed_date : undefined;

                const upcomingTasks = tasks
                    .filter(t => !t.completed_date && t.scheduled_date)
                    .sort((a, b) => (a.scheduled_date!).localeCompare(b.scheduled_date!));
                const nextDate = upcomingTasks.length > 0 ? upcomingTasks[0].scheduled_date : undefined;
                
                const isOverdue = nextDate ? new Date(nextDate) < new Date() : false;

                return {
                    ...asset,
                    lastMaintenanceDate: lastDate,
                    nextMaintenanceDate: nextDate,
                    overdue: isOverdue
                };
            }));
            setData(rows);
        } catch (err) {
            console.error('Unable to load assets', err);
            setData([]);
            setError('Assets require connectivity to load from the server.');
        } finally {
            setLoading(false);
        }
    };
    load();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    // 1. Filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(r => 
        r.name.toLowerCase().includes(lower) || 
        r.brand.toLowerCase().includes(lower) ||
        r.serial_number?.toLowerCase().includes(lower)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }

    // 2. Sort
    result.sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];

      // Handle undefined dates for sorting
      if (sortField === 'lastMaintenanceDate' || sortField === 'nextMaintenanceDate') {
          // If sorting dates, treat undefined as very old (asc) or very new (desc) depending on desire
          // Usually easier to treat as empty string for comparison
          valA = valA || '';
          valB = valB || '';
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [data, searchTerm, statusFilter, sortField, sortDirection]);

  const getStatusVariant = (status: AssetStatus) => {
      switch(status) {
          case AssetStatus.Active: return 'success';
          case AssetStatus.Maintenance: return 'warning';
          case AssetStatus.Sold: return 'neutral';
          case AssetStatus.Retired: return 'danger';
          default: return 'neutral';
      }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-usda-600 ml-1" />
      : <ArrowDown className="h-4 w-4 text-usda-600 ml-1" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Truck className="h-6 w-6 mr-2 text-usda-600" /> 
            Assets & Equipment
          </h1>
          <p className="mt-1 text-sm text-gray-500">Inventory tracking with maintenance schedules.</p>
        </div>
        {privileges.assets?.write&&<div className="mt-4 sm:mt-0">
           <Link to="/assets/new">
             <Button>
               <Plus className="h-4 w-4 mr-2" /> Add Asset
             </Button>
           </Link>
        </div>}
      </div>

      {error && (
        <div className="p-4 rounded-md bg-red-50 text-red-700 border border-red-100">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading assets from the server...</div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4 justify-between">
                <div className="relative max-w-sm w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search equipment..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    >
                        <option value="all">All Statuses</option>
                        {Object.values(AssetStatus).map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th 
                                scope="col" 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('name')}
                            >
                                <div className="flex items-center">Asset / Brand <SortIcon field="name" /></div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('status')}
                            >
                                 <div className="flex items-center">Status <SortIcon field="status" /></div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('lastMaintenanceDate')}
                            >
                                 <div className="flex items-center">Last Service <SortIcon field="lastMaintenanceDate" /></div>
                            </th>
                            <th 
                                scope="col" 
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('nextMaintenanceDate')}
                            >
                                 <div className="flex items-center">Next Scheduled <SortIcon field="nextMaintenanceDate" /></div>
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAndSortedData.map((row) => (
                            <tr 
                                key={row.asset_id} 
                                className="hover:bg-gray-50 cursor-pointer group"
                                onClick={() => navigate(`/assets/${row.asset_id}`)}
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10 bg-usda-100 rounded-full flex items-center justify-center text-usda-600">
                                            <Truck className="h-5 w-5" />
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">{row.name}</div>
                                            <div className="text-sm text-gray-500">{row.brand} {row.model_year && `• ${row.model_year}`}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Badge variant={getStatusVariant(row.status)}>{row.status}</Badge>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {row.lastMaintenanceDate ? (
                                        <span className="flex items-center">
                                            <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                                            {row.lastMaintenanceDate}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 italic">None recorded</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {row.nextMaintenanceDate ? (
                                        <span className={`flex items-center font-medium ${row.overdue ? 'text-red-600' : 'text-gray-900'}`}>
                                            {row.overdue && <AlertCircle className="h-3 w-3 mr-1" />}
                                            {row.nextMaintenanceDate}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 italic">None scheduled</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link 
                                        to={`/assets/${row.asset_id}`} 
                                        className="text-usda-600 hover:text-usda-900 flex items-center justify-end"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Details <ChevronRight className="h-4 w-4 ml-1" />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        {filteredAndSortedData.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                    No assets found matching your filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default AssetList;
